import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AppContext';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Search, UserCheck, Eye, EyeOff, X, Building2, ShieldCheck, Mail, Phone, ExternalLink } from 'lucide-react';
import axios from 'axios';

const EMPTY_FORM = {
  user_main_id: '',
  name: '',
  email: '',
  phone: '',
  password: '',
  panelRoles: [] // Array of { panel_id, role_id }
};

export default function Employees() {
  const { api } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [extEmployees, setExtEmployees] = useState([]); // from external API
  const [roles, setRoles] = useState([]);
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extLoading, setExtLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [delLoad, setDelLoad] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [viewEmp, setViewEmp] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, r, p] = await Promise.all([
        api.get('/admin/employees'),
        api.get('/admin/roles'),
        api.get('/admin/panels'),
      ]);
      setEmployees(e.data);
      setRoles(r.data);
      setPanels(p.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadExternalEmployees = async () => {
    setExtLoading(true);
    try {
      const res = await axios.get('https://user.jobes24x7.com/api/employee_education/combined-list');
      if (res.data?.data?.data) {
        setExtEmployees(res.data.data.data);
      }
    } catch (err) {
      toast.error('Failed to fetch external employees');
    } finally {
      setExtLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadExternalEmployees();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowPw(false);
    setModal(true);
  };

  const openEdit = async emp => {
    setEditing(emp);
    try {
      const res = await api.get(`/admin/employees/${emp.id}`);
      const data = res.data;
      setForm({
        user_main_id: data.user_main_id,
        panelRoles: data.panelRoles || []
      });
      setShowPw(false);
      setModal(true);
    } catch (err) {
      toast.error('Failed to load employee details');
    }
  };

  const handleExtEmpSelect = (val) => {
    if (!val) {
      setForm(EMPTY_FORM);
      return;
    }
    const selected = extEmployees.find(e => String(e.user_main_id) === String(val));
    if (selected) {
      setForm(f => ({
        ...f,
        user_main_id: selected.user_main_id,
        name: selected.user_name || `Employee ${selected.user_main_id}`,
        email: selected.email,
        password: selected.password || 'password123',
        phone: selected.phone_number
      }));
    }
  };

  const addPanelRole = () => {
    setForm(f => ({
      ...f,
      panelRoles: [...f.panelRoles, { panel_id: '', role_id: '' }]
    }));
  };

  const removePanelRole = (idx) => {
    setForm(f => ({
      ...f,
      panelRoles: f.panelRoles.filter((_, i) => i !== idx)
    }));
  };

  const updatePanelRole = (idx, field, val) => {
    setForm(f => {
      const newList = [...f.panelRoles];
      newList[idx] = { ...newList[idx], [field]: val };
      // If panel changes, clear role
      if (field === 'panel_id') newList[idx].role_id = '';
      return { ...f, panelRoles: newList };
    });
  };

  const save = async e => {
    e.preventDefault();
    if (!form.panelRoles.length) return toast.error('Assign at least one panel and role');
    if (form.panelRoles.some(pr => !pr.panel_id || !pr.role_id)) return toast.error('Complete all panel/role assignments');

    setSaving(true);
    try {
      const payload = {
        ...form,
        panelRoles: form.panelRoles.map(pr => ({
          panel_id: Number(pr.panel_id),
          role_id: Number(pr.role_id)
        }))
      };

      if (editing) {
        await api.put(`/admin/employees/${editing.id}`, payload);
        toast.success('Employee updated');
      } else {
        await api.post('/admin/employees', payload);
        toast.success('Employee created');
      }
      setModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error occurred');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDelLoad(true);
    try {
      await api.delete(`/admin/employees/${deleting.id}`);
      toast.success('Employee deleted');
      setDeleting(null);
      load();
    } catch {
      toast.error('Failed to delete employee');
    } finally {
      setDelLoad(false);
    }
  };

  const mergedEmployees = employees.map(emp => {
    const external = extEmployees.find(ext => String(ext.user_main_id) === String(emp.user_main_id));
    return {
      ...emp,
      name: external?.user_name || external?.name || 'Unknown',
      email: external?.email || 'No Email',
      phone: external?.phone_number || external?.phone || ''
    };
  });

  const filtered = mergedEmployees.filter(e =>
    e.name?.toLowerCase().includes(search.toLowerCase()) ||
    e.email?.toLowerCase().includes(search.toLowerCase()) ||
    e.user_main_id?.toString().includes(search.toLowerCase())
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Employees</h1>
          <p className="page-subtitle">Manage employee access across multiple panels and roles.</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={15} /> New Employee</button>
      </div>

      <div className="search-bar mb-6" style={{ maxWidth: 320 }}>
        <Search size={15} color="var(--text-muted)" />
        <input placeholder="Search name, email or ID…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div className="flex-center" style={{ minHeight: 200 }}><span className="spinner" style={{ width: 32, height: 32 }} /></div>
      ) : (
        <div className="card">
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Internal ID</th>
                  <th>Panels/Roles</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(emp => (
                  <tr key={emp.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="user-avatar" style={{ width: 36, height: 36, fontSize: '.8rem' }}>
                          {emp.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{emp.name}</div>
                          <div className="text-muted text-xs flex items-center gap-1">
                            <Mail size={12} /> {emp.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="text-sm font-mono text-muted">{emp.user_main_id}</div>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        <span className="badge badge-panel" style={{ background: 'var(--brand-light)', color: 'var(--brand)' }}>
                          {emp.panel_count} Assigned
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setViewEmp(emp)}><Eye size={14} /></button>
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(emp)}><Edit2 size={14} /></button>
                        <button className="btn btn-ghost btn-sm btn-icon text-danger" onClick={() => setDeleting(emp)}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={5}>
                      <div className="empty-state">
                        <UserCheck size={40} />
                        <h3>No employees found</h3>
                        <p>Assign employees to your panels here.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Edit Employee' : 'Add New Employee'} size="modal-xl">
        <form onSubmit={save}>
          <div className="modal-body overflow-y-auto" style={{ maxHeight: '75vh' }}>
            {!editing && (
              <div className="form-group">
                <label className="form-label">Select from Jobes24x7 API <span className="req">*</span></label>
                <div className="flex gap-2">
                  <select className="form-select flex-1" value={form.user_main_id} onChange={e => handleExtEmpSelect(e.target.value)} required={!editing}>
                    <option value="">— Select Employee —</option>
                    {extEmployees.map((e, idx) => (
                      <option key={`${e.user_main_id}-${idx}`} value={e.user_main_id}>
                        {e.user_name || 'Unknown'} - {e.user_main_id} ({e.type || 'Other'})
                      </option>
                    ))}
                  </select>
                  <button type="button" className="btn btn-secondary btn-icon" onClick={loadExternalEmployees} disabled={extLoading}>
                    <ExternalLink size={14} className={extLoading ? 'animate-spin' : ''} />
                  </button>
                </div>
                <p className="text-xs text-muted mt-1">Fetches latest employees from Jobes24x7 external system.</p>
              </div>
            )}

            {editing && (
              <div className="bg-muted/10 p-4 rounded-xl border border-border mt-4 mb-6">
                <div className="flex items-center gap-4">
                  <div className="user-avatar" style={{ width: 44, height: 44 }}>
                    {form.user_main_id?.charAt(0)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      {mergedEmployees.find(e => String(e.user_main_id) === String(form.user_main_id))?.name || 'Loading details...'}
                    </div>
                    <div className="text-muted text-sm">Main ID: {form.user_main_id}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="divider my-4" />

            <div className="flex justify-between items-center mb-4">
              <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>Panel &amp; Role Assignments</h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addPanelRole}>
                <Plus size={14} /> Add Panel
              </button>
            </div>

            <div className="space-y-3">
              {form.panelRoles.map((pr, idx) => (
                <div key={idx} className="flex gap-3 items-end bg-muted/30 p-3 rounded-lg border border-dashed border-border">
                  <div className="form-group flex-1 mb-0">
                    <label className="form-label text-xs">Panel</label>
                    <select className="form-select" value={pr.panel_id} onChange={e => updatePanelRole(idx, 'panel_id', e.target.value)} required>
                      <option value="">— Select Panel —</option>
                      {panels.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group flex-1 mb-0">
                    <label className="form-label text-xs">Role</label>
                    <select className="form-select" value={pr.role_id} onChange={e => updatePanelRole(idx, 'role_id', e.target.value)} required disabled={!pr.panel_id}>
                      <option value="">— Select Role —</option>
                      {roles.filter(r => String(r.panel_id) === String(pr.panel_id)).map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                  <button type="button" className="btn btn-ghost btn-icon text-danger mb-1" onClick={() => removePanelRole(idx)}>
                    <X size={16} />
                  </button>
                </div>
              ))}
              {form.panelRoles.length === 0 && (
                <div className="p-8 text-center border-2 border-dashed border-border rounded-xl text-muted">
                  <Building2 size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No panels assigned yet. Click "Add Panel" to start.</p>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner" /> : editing ? 'Update Employee' : 'Create Employee'}
            </button>
          </div>
        </form>
      </Modal>

      {/* View Details Modal */}
      <Modal open={!!viewEmp} onClose={() => setViewEmp(null)} title="Employee Overview">
        {viewEmp && (
          <div className="modal-body space-y-4">
            <div className="flex items-center gap-4 pb-4 border-b border-border">
              <div className="user-avatar" style={{ width: 56, height: 56, fontSize: '1.2rem' }}>
                {viewEmp.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 800 }}>{viewEmp.name}</h2>
                <div className="text-muted text-sm">{viewEmp.email}</div>
                {viewEmp.phone && <div className="text-muted text-xs">{viewEmp.phone}</div>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-muted/20 rounded-lg">
                <div className="text-xs text-muted mb-1">User Main ID</div>
                <div className="font-mono font-bold">{viewEmp.user_main_id}</div>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                <Building2 size={16} /> Assigned Panels &amp; Roles
              </h4>
              <div className="space-y-2">
                {employees.find(e => e.id === viewEmp.id)?.panelRoles?.map((pr, i) => (
                  <div key={i} className="flex justify-between items-center p-3 border border-border rounded-lg bg-card shadow-sm">
                    <div className="font-bold text-sm">{pr.panel_name}</div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={14} className="text-brand" />
                      <span className="text-sm font-medium">{pr.role_name}</span>
                    </div>
                  </div>
                )) || (
                    <div className="text-sm text-center py-4 bg-muted/10 rounded border border-dashed border-border">
                      Loading assignments...
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        loading={delLoad}
        title="Remove Employee"
        message={`Are you sure you want to remove "${deleting?.name}"? This action can be undone later via Admin Console.`}
      />
    </>
  );
}
